import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, CanLoad, Route, Router } from '@angular/router';
import { UserProfile } from 'src/shared/models/user-profile.model';
import { Sandbox } from 'src/app';
import { PlatformService } from 'src/shared/services/platform.service';
import { map, Observable } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { RouteData } from '../../models/route-data.model';

@Injectable()
class AuthorizationGuard implements CanLoad, CanActivate {

	constructor(private $: Sandbox,
		private _platformService: PlatformService,
		private _oidcSecurityService: OidcSecurityService,
		private _router: Router) { }

	private checkAuthAndAccess(routeData: any, user: UserProfile, returnUrl: string): boolean | Observable<boolean> {
		let routeDataUsed = routeData.roles?.roles ? routeData.roles : routeData;
		if (!routeDataUsed.roles) return true;  // no role needed to acceess this route
		if (routeDataUsed.roles.None) return true;  // no role needed to acceess this route
		
		this._oidcSecurityService.checkAuth().subscribe(({ isAuthenticated, userData, accessToken }) => {
            if (!isAuthenticated) {
                this._router.navigateByUrl('/login');
            }
        });    

		// If the user object isn't populated, wait on the profile to load
		// Navigate appropraitely on failure to auth (HAP login page) or role not matchced (unauthorized)
		if (!user || user.userId < 0 || !user.roles || !user.roles.length) {
			return this._platformService.userProfile.pipe(map((profile) => {
				if (!profile) {
					this._router.navigate(['unauthorized']);
				}
				var inRole = (!!profile.roles.find((r) => (routeDataUsed.roles[r])));
				if (!inRole) { this._router.navigate(['unauthorized']); }
				return inRole;
			}));
		}

		let hasSpecificRole = false;
		for (let role of user.roles) {
			hasSpecificRole = this.canActivateRoute(role, routeDataUsed);
			if (hasSpecificRole) { break; }
		}

		if (!hasSpecificRole) {
			this._router.navigate(['unauthorized']);
		}
		return hasSpecificRole;
	}

	canLoad(route: Route): Observable<boolean> | boolean {
		let canActivate = this.checkAuthAndAccess(route, this.$.user, null);
		return canActivate;
	}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
		let canActivate = this.checkAuthAndAccess(route.data, this.$.user, state.url);
		return canActivate;
	}

	canActivateRoute(role: string, routeData: RouteData) {
		switch (role) {
			case "HuronAdministrator":
				return routeData.roles?.HuronAdministrator;
			case "CorporationAdministrator":
				return routeData.roles?.CorporationAdministrator;
			case "UserAdministrator":
				return routeData.roles?.UserAdministrator;
			case "ClientAdministrator":
				return routeData.roles?.ClientAdministrator;
			case "User":
				return routeData.roles?.User;
			case "None":
				return routeData.roles?.None;
			default:
				return false;
		}
	}

}

export { AuthorizationGuard };
